import {useRef, useState, useCallback, useMemo} from 'react';

import {
  createCultureAgentCoreRequest,
  cultureAgentCorePaths,
  obtainCultureAgentSession,
  refreshCultureAgentSessionOrThrow,
} from '../api';
import {ChatStreamMeta, CultureAgentConfig, CultureAgentError} from '../types';

const base64ToUtf8 = (base64: string): string => {
  const binaryString = atob(base64);
  const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

const toError = (code: string, message: string): CultureAgentError => ({code, message});

export const useCultureAgentStream = (config: CultureAgentConfig) => {
  const {apiHost, sessionId: initialSessionId} = config;

  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<CultureAgentError | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId ?? null);

  const controllerRef = useRef<AbortController | null>(null);

  const obtainSession = useCallback(async (): Promise<string> => {
    const data = await obtainCultureAgentSession(apiHost, sessionId);
    setSessionId(data.session_id);
    return data.session_id;
  }, [apiHost, sessionId]);

  const refreshSession = useCallback(
    () => refreshCultureAgentSessionOrThrow(apiHost),
    [apiHost]
  );

  const coreRequest = useMemo(() => createCultureAgentCoreRequest(apiHost, refreshSession), [apiHost, refreshSession]);

  const streamMessage = useCallback(
    async (
      message: string,
      onToken: (token: string) => void,
      language: string
    ): Promise<ChatStreamMeta | undefined> => {
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      setError(null);
      setStreaming(true);

      try {
        const response = await coreRequest(cultureAgentCorePaths.stream, {
          method: 'POST',
          headers: {
            'Accept-Language': language,
            'X-Chat-Language': language,
          },
          body: JSON.stringify({message}),
          signal,
        });

        if (!response.ok || !response.body) {
          throw toError('STREAM_ERROR', `Stream request failed: ${response.statusText}`);
        }

        const certaintyHeader = response.headers.get('X-Certainty');
        const doneHeader = response.headers.get('X-Done');

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let partialData = '';

        for (;;) {
          const {done, value} = await reader.read();
          if (done) break;

          partialData += decoder.decode(value, {stream: true});
          const events = partialData.split('data: ');
          partialData = events.pop() ?? '';

          for (const event of events) {
            const chunk = event.trim();
            if (!chunk) continue;
            try {
              onToken(base64ToUtf8(chunk));
            } catch {
              console.warn('Failed to decode chunk:', chunk);
            }
          }
        }

        return {
          certainty: certaintyHeader ? parseInt(certaintyHeader, 10) : null,
          done: doneHeader === '1',
        };
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;

        const agentError =
          (err as CultureAgentError).code !== undefined
            ? (err as CultureAgentError)
            : toError('STREAM_ERROR', err instanceof Error ? err.message : 'Unknown streaming error');

        setError(agentError);
      } finally {
        setStreaming(false);
      }
    },
    [coreRequest]
  );

  const resetSurvey = useCallback(async (): Promise<void> => {
    const response = await coreRequest(cultureAgentCorePaths.reset, {method: 'GET'});
    if (!response.ok) {
      setError(toError('RESET_ERROR', `Failed to reset survey: ${response.statusText}`));
    }
  }, [coreRequest]);

  const abort = useCallback(() => {
    controllerRef.current?.abort();
    setStreaming(false);
  }, []);

  return {obtainSession, streamMessage, resetSurvey, abort, streaming, error, sessionId};
};
